import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import Footer from "../components/Footer";
import { QA_TOPICS } from "../lib/qaTopic";
import {
  Anchor, Layers, Square, Smile, Activity, Gauge, Users,
  FlaskConical, Mail, FileSignature, DollarSign, Stethoscope, ExternalLink, Menu, X,
} from "lucide-react";

const heroBg =
  "https://images.unsplash.com/photo-1777444969135-caf869407707?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const MODULES = [
  { name: "Implant Case Planner", Icon: Anchor },
  { name: "Crown & Bridge Designer", Icon: Layers },
  { name: "Inlay/Onlay Advisor", Icon: Square },
  { name: "Smile Design Planner", Icon: Smile },
  { name: "Full-Arch All-on-X", Icon: Activity },
  { name: "Occlusion Analyzer", Icon: Gauge },
  { name: "Denture & RPD Planner", Icon: Users },
  { name: "Material Science Advisor", Icon: FlaskConical },
  { name: "Referral Letter Generator", Icon: Mail },
  { name: "Patient Consent Generator", Icon: FileSignature },
  { name: "Case Fee Estimator", Icon: DollarSign },
];

// Pricing has been relocated to the authenticated /plans-membership page.
// Public landing intentionally shows no dollar amounts, plan cards, or
// pricing navigation — clinician-approved policy 2026-08.

export default function Landing() {
  const { user } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen" style={{ background: "var(--pa-bg)" }}>
      {/* Nav */}
      <header
        className="fixed top-0 inset-x-0 z-30 bg-[var(--pa-bg)]/85 backdrop-blur-xl border-b border-[var(--pa-border)]"
        style={{
          paddingTop: "env(safe-area-inset-top)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-[10px] min-w-0" data-testid="landing-brand">
            <img
              src="/brand/camelionai-emblem-approved.png"
              alt="CamelionAI emblem"
              width="40"
              height="40"
              decoding="async"
              className="shrink-0 h-[40px] w-[40px] sm:h-[44px] sm:w-[44px] object-contain"
              style={{ aspectRatio: "1 / 1" }}
            />
            <div className="flex flex-col leading-none min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className="font-serif text-[22px] sm:text-[26px] text-[var(--pa-text)]">Prostho</span>
                <span className="font-serif text-[22px] sm:text-[26px] text-[var(--pa-gold)]">AI</span>
              </div>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.18em] text-[var(--pa-text-dim)] mt-0.5">
                by CamelionAI
              </span>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-[var(--pa-text-muted)]">
            <a href="#modules" className="hover:text-[var(--pa-gold)] transition">Modules</a>
            <a href="#about" className="hover:text-[var(--pa-gold)] transition">About</a>
            <a
              href="https://camelionai.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-camelionai"
              className="inline-flex items-center gap-1 hover:text-[var(--pa-gold)] transition"
            >
              CamelionAI <ExternalLink size={11} />
            </a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition px-2 py-2">
              Sign in
            </Link>
            <Link
              to="/signup"
              data-testid="nav-signup"
              className="text-sm bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-2.5 sm:px-4 py-2 rounded-md hover:bg-[var(--pa-gold-hover)] transition whitespace-nowrap"
            >
              Get started
            </Link>
            <button
              type="button"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label="Open menu"
              aria-expanded={mobileNavOpen}
              data-testid="landing-mobile-menu-toggle"
              className="md:hidden tap-target text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] -mr-2"
            >
              {mobileNavOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div
            data-testid="landing-mobile-nav"
            className="md:hidden border-t border-[var(--pa-border)] bg-[var(--pa-bg-2)]/95 backdrop-blur-xl"
          >
            <nav className="px-4 py-3 flex flex-col text-base text-[var(--pa-text-muted)]">
              {[
                { href: "#modules", label: "Modules" },
                { href: "#about", label: "About" },
              ].map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileNavOpen(false)}
                  className="py-3 border-b border-[var(--pa-border)] hover:text-[var(--pa-gold)] transition"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/login"
                onClick={() => setMobileNavOpen(false)}
                data-testid="landing-mobile-signin"
                className="py-3 border-b border-[var(--pa-border)] hover:text-[var(--pa-gold)] transition"
              >
                Sign in
              </Link>
              <a
                href="https://camelionai.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileNavOpen(false)}
                className="py-3 inline-flex items-center gap-1.5 hover:text-[var(--pa-gold)] transition"
              >
                CamelionAI <ExternalLink size={12} />
              </a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--pa-bg)]/70 via-[var(--pa-bg)]/85 to-[var(--pa-bg)]" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="kicker mb-6" data-testid="hero-kicker">
              PROSTHODONTIST-DESIGNED · FOR PROSTHODONTISTS
            </div>
            <h1
              className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[var(--pa-text)] leading-[1.05]"
              data-testid="hero-title"
            >
              Specialist-Grade AI
              <span className="text-[var(--pa-gold)]"> for Prosthodontics.</span>
            </h1>
            <p className="mt-7 text-base lg:text-lg text-[var(--pa-text-muted)] max-w-2xl leading-relaxed" data-testid="hero-sub">
              The only AI platform built exclusively for prosthodontists — clinical analysis,
              treatment planning, and documentation at specialist speed.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={user ? "/dashboard" : "/signup"}
                data-testid="hero-cta-try-prosthoai"
                className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-7 py-3.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
              >
                Try ProsthoAI
              </Link>
              <Link
                to={user ? "/quick-assist" : "/signup"}
                data-testid="hero-cta-try-aria"
                className="border border-[var(--pa-gold)] text-[var(--pa-gold)] font-semibold px-7 py-3.5 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
              >
                Try ARIA
              </Link>
            </div>

            {/* Pricing-disclosure callout — visually secondary to the
                hero. Public landing intentionally shows no dollar
                amounts; this callout points signed-in clinicians to
                /plans-membership and signed-out visitors to /signup. */}
            <aside
              data-testid="pricing-disclosure-callout"
              className="mt-6 max-w-2xl border border-[var(--pa-gold)]/70 rounded-md bg-transparent p-4 sm:p-5"
            >
              <div
                className="text-sm font-semibold text-[var(--pa-gold)]"
                data-testid="pricing-disclosure-headline"
              >
                Explore the features. Then review full pricing.
              </div>
              <p
                className="mt-2 text-xs sm:text-sm text-[var(--pa-text-muted)] leading-relaxed"
                data-testid="pricing-disclosure-body"
              >
                See how ProsthoAI applies prosthodontic expertise to
                support informed clinical decision-making. Then sign in
                to view full membership and specialist-review fees and
                compare the options that best fit your practice. No
                payment is required to review the plans.
              </p>
              <Link
                to={user ? "/plans-membership" : "/signup"}
                data-testid="pricing-disclosure-cta"
                className="mt-3 inline-block text-xs sm:text-sm font-semibold text-[var(--pa-gold)] hover:underline underline-offset-2"
              >
                View plans &amp; fees →
              </Link>
            </aside>

            {/* Try a Quick Assessment — public entry (no anonymous AI access).
                Each pill routes to /signup?topic=<slug>; the slug persists
                through auth via /app/frontend/src/lib/qaTopic.js. */}
            <section
              data-testid="try-qa-section"
              aria-labelledby="try-qa-heading"
              className="mt-10"
            >
              <h2
                id="try-qa-heading"
                data-testid="try-qa-heading"
                className="font-serif text-2xl sm:text-3xl text-[var(--pa-text)]"
              >
                Try a Quick Assessment
              </h2>
              <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
                Pick a starting point. Sign in to see the full clinical workflow — no anonymous AI access.
              </p>
              <ul
                data-testid="try-qa-list"
                className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap"
              >
                {QA_TOPICS.map((t) => (
                  <li key={t.slug} className="w-full sm:w-auto">
                    <Link
                      to={`/signup?topic=${t.slug}`}
                      data-testid={`try-qa-${t.slug}`}
                      className="flex items-center justify-between gap-3 w-full sm:w-auto min-h-[44px] px-5 py-3 rounded-full border-2 border-[var(--pa-gold)] text-[var(--pa-gold)] font-semibold text-sm hover:bg-[var(--pa-gold)] hover:text-[var(--pa-bg)] transition"
                    >
                      <span>{t.label}</span>
                      <span aria-hidden="true" className="opacity-70">→</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>

            {/* Trust bar — high-contrast scrim over the dental image.
                Meets WCAG AA 4.5:1 with white/gold on the near-opaque dark
                surface; keeps wording unchanged and compact. Inline style is
                used because Tailwind's opacity modifier (bg-[var(...)]/90)
                does not reliably resolve for CSS-variable arbitrary values,
                which produced a fully transparent computed background. */}
            <div
              data-testid="trust-bar"
              style={{ backgroundColor: "rgba(10, 10, 10, 0.92)" }}
              className="mt-8 inline-flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 rounded-md backdrop-blur-sm border border-[var(--pa-border)] text-[11px] uppercase tracking-[0.18em] text-white"
            >
              <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--pa-gold)]">
                Patent Pending USPTO #64/062,147
              </span>
              <span className="text-[var(--pa-gold)]/60">·</span>
              <span className="text-white">Built with HIPAA safeguards</span>
              <span className="text-[var(--pa-gold)]/60">·</span>
              <span className="text-white">Built by Dr. Ronik Seecharan DMD</span>
              <span className="text-[var(--pa-gold)]/60">·</span>
              <span className="text-white">Prosthodontist-designed</span>
            </div>

            <div className="mt-6 flex items-center gap-3 text-xs text-[var(--pa-text-dim)]">
              <Stethoscope size={14} className="text-[var(--pa-gold)]" />
              Built by a clinician, for clinicians — 30 years of prosthodontic practice dedicated to treatment planning excellence.
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[var(--pa-border-active)] rounded-lg bg-[var(--pa-surface)] p-8">
              <div className="kicker mb-4">Specialty Coverage</div>
              <div className="grid grid-cols-2 gap-3">
                {MODULES.map(({ name, Icon }) => (
                  <div
                    key={name}
                    className="flex items-center gap-2.5 text-sm text-[var(--pa-text-muted)] py-1.5"
                  >
                    <Icon size={14} className="text-[var(--pa-gold)] shrink-0" />
                    <span className="truncate">{name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Signature Feature: Complete Case Analysis */}
      <section className="py-24 border-t border-[var(--pa-border)] bg-[var(--pa-bg-2)]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="kicker text-[var(--pa-gold)] mb-4">
                The Signature Capability
              </div>
              <h2 className="font-serif text-3xl lg:text-5xl text-[var(--pa-text)] leading-[1.1]">
                Complete Case Analysis
              </h2>
              <p className="mt-4 text-base lg:text-lg text-[var(--pa-text-muted)] leading-relaxed">
                A focused complaint — "I need an implant at #19" — should never get a focused
                answer. ProsthoAI evaluates the patient the way a prosthodontist would:
                facial proportions, vertical dimension, smile esthetics, and occlusion together,
                then the rest of the mouth, then your original question — answered in full context.
              </p>
              <ol className="mt-7 space-y-3 text-sm text-[var(--pa-text-muted)]">
                {[
                  ["1", "State your specific clinical concern"],
                  ["2", "Facial and occlusal analysis run first — the foundation"],
                  ["3", "Full-mouth findings tagged Urgent · Important · Monitor"],
                  ["4", "Three treatment pathways: Comprehensive · Sequential · Limited"],
                  ["5", "Your original question, answered in full clinical context"],
                  ["6", "Immutable case record for liability documentation"],
                ].map(([n, t]) => (
                  <li key={n} className="flex items-start gap-3">
                    <span className="font-serif text-[var(--pa-gold)] w-5 shrink-0">{n}</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="lg:col-span-5">
              <div className="border-2 border-[var(--pa-gold)] rounded-lg bg-[var(--pa-surface)] p-8">
                <div className="kicker text-[var(--pa-gold)]">Foundation Principle</div>
                <p className="mt-3 font-serif text-xl text-[var(--pa-text)] leading-snug">
                  Every case begins with facial and occlusal analysis. Lip support, vertical
                  dimension, and smile esthetics are read alongside the bite as the foundation
                  of every treatment plan. Every clinical decision is documented.
                </p>
                <div className="gold-divider my-6" />
                <p className="text-xs text-[var(--pa-text-dim)]">
                  Patent Pending USPTO #64/062,147 · CamelionAI LLC, Boca Raton FL ·
                  Built by Dr. Ronik Seecharan, DMD — Prosthodontist &amp; Founder
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section id="modules" className="py-24 border-t border-[var(--pa-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="kicker mb-4">11 Clinical Modules</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-[var(--pa-text)] max-w-3xl">
            Specialist depth on every case — single crown to full-arch rehabilitation,
            documented at chairside speed.
          </h2>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {MODULES.map(({ name, Icon }) => (
              <div
                key={name}
                data-testid={`landing-module-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="module-card bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-6 transition-all duration-300"
              >
                <Icon size={22} className="text-[var(--pa-gold)] mb-4" />
                <div className="font-serif text-lg text-[var(--pa-text)]">{name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing sections removed 2026-08 — see /plans-membership (authenticated). */}

      {/* About */}
      <section id="about" className="py-24 border-t border-[var(--pa-border)]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="kicker mb-4">About</div>
          <h2 className="font-serif text-3xl lg:text-4xl text-[var(--pa-text)]">
            Built by a clinician, for clinicians.
          </h2>
          <p className="mt-6 text-base text-[var(--pa-text-muted)] max-w-2xl mx-auto leading-relaxed">
            ProsthoAI was designed around one principle:{" "}
            <span className="text-[var(--pa-text)]">the way a prosthodontist thinks.</span>{" "}
            Every case begins with facial and occlusal analysis — facial proportions, lip
            support, vertical dimension, and smile esthetics are read alongside occlusion as
            the foundation of every treatment plan. Every clinical decision is documented.
          </p>
          <p className="mt-5 text-sm text-[var(--pa-text-muted)] max-w-2xl mx-auto leading-relaxed">
            Built by <span className="text-[var(--pa-text)]">Dr. Ronik Seecharan, DMD</span> —
            Prosthodontist &amp; Founder, <span className="text-[var(--pa-text)]">CamelionAI LLC</span>, Boca Raton,
            Florida. 30 years of clinical practice dedicated to treatment planning excellence,
            now available chairside to every prosthodontist.
          </p>
          <div className="mt-8 inline-block kicker text-[var(--pa-gold)]">
            Patent Pending USPTO #64/062,147
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
